import { MessagePanel, ProgressSpinner, useDetectTheme } from '@dittolive/anvil'
import { useAppToDatabaseRename } from 'components/hooks'
import { useContext, useLayoutEffect, useState } from 'react'

import AppSettingsContext from './context'

export const EXPECTED_INGRESS_DOMAIN_READY_TIME_MS = 30000
export const STEP_MS = 200

/** Show a loading bar for the ingress domain loading status, or an error panel if the ingress
 * domain is in error state. */
const IngressDomainLoadingStatus = () => {
  const {
    isDomainReady,
    isDomainInError,
    isDomainInitiallyReady,
    queryAttempts,
  } = useContext(AppSettingsContext)

  const { databaseText } = useAppToDatabaseRename()

  const [timeLoading, setTimeLoading] = useState(0)
  const theme = useDetectTheme()
  const didStartDomainDuringSession =
    !isDomainInitiallyReady && !!queryAttempts && isDomainReady

  useLayoutEffect(() => {
    if (
      !isDomainReady &&
      !isDomainInError &&
      timeLoading < EXPECTED_INGRESS_DOMAIN_READY_TIME_MS
    ) {
      const timeoutId = setTimeout(() => {
        setTimeLoading((curentTimeLoading) => curentTimeLoading + STEP_MS)
      }, STEP_MS)

      return () => clearTimeout(timeoutId)
    }
  }, [timeLoading, isDomainReady, isDomainInError])

  if (isDomainInError) {
    return (
      <div className="pb-4">
        <MessagePanel
          message={`We've encountered an issue configuring your Big Peer and we're working to resolve it. Big Peer settings for your ${databaseText} are temporarily unavailable. Please check back later.`}
          variant="danger"
        />
      </div>
    )
  }

  if (didStartDomainDuringSession) {
    return (
      <div className="pb-4">
        <MessagePanel
          message="Your Big Peer is configured. You can now access Big Peer specific areas like the DQL Editor, Authentication Mode & Webhook Settings and Live Query Settings."
          variant="success"
        />
      </div>
    )
  }

  if (isDomainReady || isDomainInitiallyReady || !queryAttempts) {
    return null
  }

  return (
    <div className="pb-4">
      <div className="border-l-border-normal bg-background-surface flex rounded-none border-l-2 p-3">
        <div className="h-6 w-6">
          <ProgressSpinner
            className="h-full w-full"
            strokeWidth={3}
            total={EXPECTED_INGRESS_DOMAIN_READY_TIME_MS}
            progress={timeLoading}
            progressColor={getComputedStyle(document.documentElement)
              .getPropertyValue('--color-primary')
              .trim()}
            remainingColor={
              theme === 'light'
                ? getComputedStyle(document.documentElement)
                    .getPropertyValue('--color-primary-200')
                    .trim()
                : getComputedStyle(document.documentElement)
                    .getPropertyValue('--color-gray-600')
                    .trim()
            }
          />
        </div>
        <div className="ml-3 items-center md:flex md:justify-between">
          <p
            className="text-foreground-subtle"
            data-testid="configuringMessage"
          >
            We&apos;re setting up your Big Peer. This may take up to a few
            minutes.
          </p>
        </div>
      </div>
    </div>
  )
}

export default IngressDomainLoadingStatus
